import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
	Avatar,
	Badge,
	Backdrop,
	Box,
	Button,
	CircularProgress,
	FormControl,
	Grid,
	IconButton,
	Input,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@mui/material';
import { Cancel } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilState } from 'recoil';
import {
	adventureBackgroundImageState,
	adventureBackgroundColorState,
} from './../../common/recoil/Adventures/AdventuresAtoms';
import { userNameState, userAvatarState } from './../../common/recoil/UserInfo/atoms';
import { ConfirmDialog, DropZone } from './../../common/components';
import {
	deleteAvatar,
	getUserAvatars,
	uploadAvatar,
} from './../../common/services/avatarsRequests';
import DevicesSelector from './../DevicesSelector/DevicesSelector'
import CameraPreview from './../CameraPreview/CameraPreview'
import { debugSession } from '../../common/utils/sessionDebug';

//const useStyles = makeStyles((theme) => ({
const useStyles = (options) => {
	return makeStyles((theme) => ({
		root: {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			minHeight: '100vh',
			backgroundColor: options.backgroundColor || theme.palette.primary.main,
			backgroundImage: `url(${options.backgroundImage})`,
			backgroundSize: 'cover',
			backgroundPosition: 'center',
		},
		formContainer: {
			padding: theme.spacing(2),
			width: '500px',
			backgroundColor: theme.palette.background.default,
			textAlign: 'center',
		},
		title: {
			marginBottom: theme.spacing(5),
		},
		form: {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
		},
		avatarContainer: {
			margin: theme.spacing(2),
		},
		avatar: {
			backgroundColor: theme.palette.secondary.main,
			width: theme.spacing(7),
			height: theme.spacing(7),
			margin: theme.spacing(1),
			cursor: 'pointer',
		},
		avatarSelected: {
			border: `3px solid ${theme.palette.primary.contrastText}`,
		},
		inputContainer: {
			margin: theme.spacing(2),
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
		},
		inputAdornment: {
			color: theme.palette.text.primary,
		},
		inputLabel: {
			color: theme.palette.text.primary,
		},
		input: {
			color: theme.palette.text.primary,
		},
		button: {
			margin: theme.spacing(2),
		},
	}))();
};

const StyledBadge = withStyles((theme) => ({
	badge: {
		right: 4,
		top: 8,
		border: `0px solid ${theme.palette.background.paper}`,
		padding: '0',
	},
}))(Badge);

const GameWaiting = ({ start }) => {
	const [name, setName] = useRecoilState(userNameState);
	const [selectedAvatar, setSelectedAvatar] = useRecoilState(userAvatarState);
	const [avatars, setAvatars] = useState([]);
	const [loading, setLoading] = useState(false);
	const [avatarToDelete, setAvatarToDelete] = useState();
	const [showDeleteAvatar, setShowDeleteAvatar] = useState(false);
	const adventureBackgroundImage = useRecoilValue(adventureBackgroundImageState);
	const adventureBackgroundColor = useRecoilValue(adventureBackgroundColorState);

	const { t } = useTranslation();
	useEffect(() => {
		debugSession('GameWaiting', 'mount', {
			hasBackgroundImage: Boolean(adventureBackgroundImage),
			hasBackgroundColor: Boolean(adventureBackgroundColor),
		});

		return () => {
			debugSession('GameWaiting', 'unmount');
		};
	}, []);
	const classes = useStyles({
		backgroundImage: adventureBackgroundImage,
		backgroundColor: adventureBackgroundColor,
	});

	let nameInput = useRef();

	const handleNameChange = (event) => {
		setName(event.target.value);
	};

	const handleAvatarClick = (avatar) => {
		setSelectedAvatar(avatar);
	};

	const handleStartGameClick = () => {
		debugSession('GameWaiting', 'start-click', {
			hasName: Boolean(nameInput.current?.value),
		});
		if (nameInput.current.value !== '') {
			setName(nameInput.current.value);
			start();
		}
	};

	useEffect(() => {
		if (name && nameInput.current) {
			nameInput.current.value = name;
		}
	}, [name]);

	const fetchAvatars = async () => {
		debugSession('GameWaiting', 'fetch-avatars-start');
		setLoading(true);
		const response = await getUserAvatars();
		if (response.avatars && response.avatars.length > 0) {
			// add selectedAvatar to the list of avatars
			const avatarExists = response.avatars.find((avatar) => avatar.ID === selectedAvatar?.ID);
			if (!avatarExists && selectedAvatar) {
				response.avatars.push(selectedAvatar);
			}
		}
		setAvatars(response.avatars);
		setSelectedAvatar(response?.avatars[0]);
		
		setLoading(false);
		debugSession('GameWaiting', 'fetch-avatars-success', {
			avatarCount: response?.avatars?.length || 0,
		});
	};

	const deleteAvatarById = async () => {
		setLoading(true);
		setShowDeleteAvatar(false);
		await deleteAvatar(avatarToDelete);
		await fetchAvatars();
		setLoading(false);
	};

	const onDrop = useCallback(async (file) => {
		setLoading(true);
		const newFile = await uploadAvatar(file.binary, file);
		await fetchAvatars();
		setLoading(false);
	}, []);

	useEffect(() => {
		debugSession('GameWaiting', 'fetch-avatars-effect');
		fetchAvatars();
	}, []);

	return (
		<div className={classes.root}>
			<Box className={classes.formContainer}>
				<Typography variant="h3" className={classes.title}>
					{t('gameWaiting.title')}
				</Typography>

				<TextField
					data-testid="userNameField"
					autoFocus
					margin="dense"
					id="name"
					label={t('userInfo.fields.name')}
					type="text"
					fullWidth
					inputRef={nameInput}
					onKeyPress={(event) => {
						if (event.key === 'Enter') {
							handleStartGameClick();
						}
					}}
				/>
				<form className={classes.form}>
					<div className={classes.avatarContainer}>
						<Box display="flex">
							{avatars.map((avatar) => {
								return (
									<IconButton aria-label="cart" size="large">
										<StyledBadge
											badgeContent={
												<Cancel
													className={classes.close}
													onClick={() => {
														setAvatarToDelete(avatar.ID);
														setShowDeleteAvatar(true);
													}}></Cancel>
											}
											color="secondary">
											<Avatar
												className={`${classes.avatar} ${avatar?.ID === selectedAvatar?.ID
														? classes.selectedAvatar
														: ''
													}`}
												onClick={() => {
													if (avatar != selectedAvatar) {
														setSelectedAvatar(avatar);
													} else {
														setSelectedAvatar();
													}
												}}
												src={avatar.url}
											/>
										</StyledBadge>
									</IconButton>
								);
							})}
						</Box>
						<DevicesSelector />						
						<DropZone onFileReady={onDrop}></DropZone>
						<CameraPreview />
						
						<Backdrop className={classes.backdrop} open={loading}>
							<CircularProgress color="inherit" />
						</Backdrop>
						<ConfirmDialog
							open={showDeleteAvatar}
							setOpen={setShowDeleteAvatar}
							title={t('UserInfoSelector.deleteAvatarDialog.title')}
							text={t('UserInfoSelector.deleteAvatarDialog.text')}
							successButtonText={t(
								'UserInfoSelector.deleteAvatarDialog.buttons.success'
							)}
							cancelButtonText={t(
								'UserInfoSelector.deleteAvatarDialog.buttons.cancel'
							)}
							onSuccess={deleteAvatarById}></ConfirmDialog>
					</div>
					<Button
						variant="contained"
						color="primary"
						className={classes.button}
						onClick={handleStartGameClick}>
						{t('gameWaiting.startGameButton')}
					</Button>
				</form>
			</Box>
		</div>
	);
};

export default GameWaiting;
